// Logotipo de ilender — archivo oficial, no reconstruccion.
//
// Este componente dibujaba antes el isotipo como SVG deducido del mapa de bits y
// componia al lado la palabra "iLender" en Sora. Con el manual de marca en la mano
// eso es insostenible: la pagina 13 lista como usos indebidos "no alterar la
// diagramacion", "no deformar el logotipo", "no cambiar el color" y —el que lo
// zanja— "no sustituir el texto por otras tipografias". El logotipo esta dibujado
// en Stag Sans y no se re-escribe con otra fuente. Ademas la marca es "ilender",
// enteramente en minuscula, no "iLender".
//
// Asi que el logotipo entra como el archivo que entrego el cliente. Los PNG de
// src/assets/marca/ se generaron desde `logos ilender negro.png` (el unico de los
// tres entregados que esta limpio) recoloreando sobre el canal alfa, de modo que la
// silueta es la original pixel a pixel y el color es el normativo de la pagina 10:
// R0 G116 B200.
//
// Version positiva o negativa segun el fondo, que es lo que el manual pide en las
// paginas 10 y 11: sobre el azul corporativo va el logotipo en negativo (blanco);
// sobre fondo claro o sobre los secundarios, en positivo.

import logotipoColor from '../assets/marca/ilender-color.png';
import logotipoBlanco from '../assets/marca/ilender-blanco.png';
import isotipoColor from '../assets/marca/isotipo-color.png';
import isotipoBlanco from '../assets/marca/isotipo-blanco.png';

// Relacion de aspecto medida sobre el archivo oficial. Se declara para que el
// navegador reserve el hueco antes de cargar la imagen y la cabecera no salte.
const RATIO_LOGOTIPO = 707 / 235;   // 3.009:1
const RATIO_ISOTIPO = 236 / 235;    // practicamente 1:1

/**
 * Logotipo completo (isotipo + "ilender").
 *
 * `sobreFondoOscuro` elige la version negativa. No es una preferencia estetica:
 * el manual la exige cuando el logotipo se apoya en un fondo de color.
 *
 * `alto` en px. El manual fija 1.3 cm (~49 px) como minimo de reproduccion
 * IMPRESA; en pantalla no se baja de 28 px, y por debajo de eso se usa <Isotipo>.
 */

// Las tres firmas comparten `sobreFondoOscuro` y `alto`, asi que el tipo base se
// declara una vez y cada componente anade lo suyo. `alto` va en pixeles: es lo que
// esperan `height` y el calculo del aire.
type PropsDeMarca = {
  /** Invierte el color del logotipo para fondos oscuros. */
  sobreFondoOscuro?: boolean;
  /** Alto en pixeles. */
  alto?: number;
};

type PropsConAire = PropsDeMarca & {
  /** Reserva el margen de proteccion que exige el manual de marca. */
  conAire?: boolean;
  className?: string;
};

export function Logotipo({ sobreFondoOscuro = false, alto = 40, conAire = true, className = '' }: PropsConAire) {
  return (
    <img
      src={sobreFondoOscuro ? logotipoBlanco : logotipoColor}
      alt="ilender"
      className={`logo-marca ${conAire ? 'logo-aire' : ''} ${className}`}
      style={{ height: alto, width: alto * RATIO_LOGOTIPO, fontSize: alto }}
    />
  );
}

/**
 * Isotipo suelto — la parte grafica, sin la palabra.
 *
 * El manual le dedica su propia pagina (9) con area restringida propia, asi que
 * usarlo solo es un uso previsto y no un recorte arbitrario del logotipo.
 */
export function Isotipo({ sobreFondoOscuro = false, alto = 32, conAire = false, className = '' }: PropsConAire) {
  return (
    <img
      src={sobreFondoOscuro ? isotipoBlanco : isotipoColor}
      alt="ilender"
      className={`logo-marca ${conAire ? 'logo-aire' : ''} ${className}`}
      style={{ height: alto, width: alto * RATIO_ISOTIPO, fontSize: alto }}
    />
  );
}

/**
 * Bloque de marca: logotipo + nombre del producto.
 *
 * "Farmacovigilancia" NO forma parte del logotipo — es el nombre del sistema — asi
 * que va debajo y separado por el area restringida, nunca pegado ni compuesto
 * dentro de la marca.
 */
type PropsDeBrandMark = PropsDeMarca & {
  /** Acompana el logotipo con el nombre del producto. */
  mostrarProducto?: boolean;
};

export default function BrandMark({ sobreFondoOscuro = false, alto = 40, mostrarProducto = true }: PropsDeBrandMark) {
  return (
    <div className="select-none">
      <Logotipo sobreFondoOscuro={sobreFondoOscuro} alto={alto} />
      {mostrarProducto && (
        <p className={`text-meta font-medium mt-0.5 ml-[0.45em] ${
          sobreFondoOscuro ? 'text-cian-suave' : 'text-ink-soft'}`}>
          Farmacovigilancia
        </p>
      )}
    </div>
  );
}
